// Release-specific press kit — one page per record, backed only by catalog data.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { getReleaseBySlug } from "@/lib/music.functions";
import { newsForRelease, NEWSROOM_DISCLOSURE } from "@/lib/news";
import { BOILERPLATE, BIO_150, USAGE_TERMS } from "@/lib/press";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { track } from "@/lib/analytics";
import { breadcrumbs, ldScript, releasePressKitNode, seoMeta } from "@/lib/schema";

export const Route = createFileRoute("/press/kit/$slug")({
  loader: async ({ params }) => {
    const release = await getReleaseBySlug({ data: { slug: params.slug } });
    if (!release) throw notFound();
    return { release };
  },
  head: ({ loaderData, params }) => {
    const release = loaderData?.release;
    const title = release
      ? `${release.title} Press Kit | Buddha KA · KAfrequency`
      : "Release Press Kit | Buddha KA";
    const description = release
      ? `Official press kit for ${release.title} by Buddha KA — release facts, cover artwork, track listing, and official links.`
      : "Official Buddha KA release press kit.";
    const image = release?.coverUrl ? absoluteUrl(release.coverUrl) : undefined;
    const path = `/press/kit/${params.slug}`;
    return {
      ...seoMeta({ title, description, path, ...(image ? { image, imageAlt: `${release?.title} cover artwork` } : {}) }),
      scripts: release
        ? [
            ldScript(
              releasePressKitNode({
                slug: release.slug,
                title: `${release.title} — Press Kit`,
                description,
                ...(image ? { image } : {}),
                assets: image ? [{ url: image, label: `${release.title} — cover artwork` }] : [],
              }),
            ),
            ldScript(
              breadcrumbs([
                ["Press kit", "/press"],
                [`${release.title} press kit`, path],
              ]),
            ),
          ]
        : [],
    };
  },
  component: ReleasePressKit,
});

function ReleasePressKit() {
  const { release } = Route.useLoaderData();
  const related = newsForRelease(release.slug);
  const cover = release.coverUrl ? absoluteUrl(release.coverUrl) : null;

  useEffect(() => {
    track("press_release_kit_view", { release: release.slug });
  }, [release.slug]);

  const facts: Array<[string, string]> = [
    ["Title", release.title],
    ["Artist", "Buddha KA"],
    ["Format", release.type],
    ...(release.year ? ([["Year", String(release.year)]] as Array<[string, string]>) : []),
    ...(release.releaseDate ? ([["Release date", release.releaseDate]] as Array<[string, string]>) : []),
    ["Status", release.status],
    ...(release.frequency ? ([["Frequency", release.frequency]] as Array<[string, string]>) : []),
    ["Label", "KAfrequency (artist-owned, independent)"],
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs uppercase tracking-[0.25em] text-muted-foreground">
        <Link to="/press" className="hover:text-gold">Press kit</Link> <span aria-hidden>/</span>{" "}
        <span className="text-gold">{release.title}</span>
      </nav>

      <header className="grid gap-8 md:grid-cols-[280px_1fr] md:items-start">
        {cover && (
          <img
            src={cover}
            alt={`${release.title} — cover artwork`}
            width={560}
            height={560}
            loading="eager"
            decoding="async"
            className="w-full rounded-2xl border border-gold/40 bg-charcoal object-cover shadow-halo"
          />
        )}
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Release press kit</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl">{release.title}</h1>
          {release.statement && <p className="mt-4 text-muted-foreground">{release.statement}</p>}
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Link
              to="/music/$slug"
              params={{ slug: release.slug }}
              className="rounded-full bg-gold-gradient px-5 py-2.5 font-semibold text-obsidian shadow-gold"
            >
              Listen / release page
            </Link>
            {cover && (
              <a
                href={cover}
                download
                onClick={() => track("press_kit_asset_download", { asset: `${release.slug}-cover` })}
                className="rounded-full border border-gold px-5 py-2.5 text-gold hover:bg-gold hover:text-obsidian"
              >
                Download cover artwork
              </a>
            )}
            <Link to="/contact" className="rounded-full border hairline px-5 py-2.5 hover:border-gold hover:text-gold">
              Media inquiries
            </Link>
          </div>
        </div>
      </header>

      <section className="mt-12">
        <h2 className="font-serif text-2xl">Release facts</h2>
        <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
          {facts.map(([k, v]) => (
            <div key={k} className="border-b hairline pb-2">
              <dt className="text-[10px] uppercase tracking-[0.25em] text-gold">{k}</dt>
              <dd className="mt-1 text-sm text-ivory/90">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      {release.tracks?.length ? (
        <section className="mt-12">
          <h2 className="font-serif text-2xl">Track listing</h2>
          <ol className="mt-4 space-y-2 text-sm text-ivory/90">
            {release.tracks.map((t: { slug?: string; title: string }, i: number) => (
              <li key={t.slug ?? i} className="flex gap-3 border-b hairline pb-2">
                <span className="w-6 text-muted-foreground">{i + 1}.</span>
                {t.slug ? (
                  <Link
                    to="/music/$slug/song/$song"
                    params={{ slug: release.slug, song: t.slug }}
                    className="hover:text-gold-luminous"
                  >
                    {t.title}
                  </Link>
                ) : (
                  <span>{t.title}</span>
                )}
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {release.links && Object.keys(release.links).length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl">Official links</h2>
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(Object.entries(release.links) as Array<[string, string]>)
              .filter(([, url]) => Boolean(url))
              .map(([platform, url]) => (
                <li key={platform}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => track("press_outbound_click", { platform, release: release.slug })}
                    className="flex min-h-11 items-center justify-center rounded-full border border-gold px-3 py-2 text-xs text-gold hover:bg-gold hover:text-obsidian"
                  >
                    {platform} ↗
                  </a>
                </li>
              ))}
          </ul>
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl">Related official announcements</h2>
          <ul className="mt-4 space-y-3">
            {related.map((n) => (
              <li key={n.slug} className="rounded-xl border hairline bg-charcoal p-4">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {n.category} · <time dateTime={n.publishedAt}>{n.publishedAt}</time>
                </p>
                <Link
                  to="/news/$slug"
                  params={{ slug: n.slug }}
                  className="mt-2 block font-serif text-lg text-ivory hover:text-gold-luminous"
                >
                  {n.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-12">
        <h2 className="font-serif text-2xl">Artist biography (150 words)</h2>
        <p className="mt-3 text-sm leading-relaxed text-ivory/85">{BIO_150}</p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{BOILERPLATE}</p>
      </section>

      <section className="mt-12 rounded-2xl border hairline bg-obsidian/60 p-6 text-sm text-muted-foreground">
        <h2 className="font-serif text-xl text-ivory">Asset usage</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {USAGE_TERMS.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <p className="mt-3">{NEWSROOM_DISCLOSURE}</p>
      </section>
    </div>
  );
}
